/**
 * Guscio dell'applicazione.
 *
 * Due sezioni, non dieci: **Chat** (quello che chiedi) e **Avvisi** (quello che
 * Otto ha capito da solo). Tutto il resto — oggi, immobili, messaggi, consigli,
 * storico, impostazioni — vive dietro le iconcine sopra la chat e si apre in
 * una finestra larga, senza farti perdere il posto in cui eri.
 *
 * La regola dietro la scelta: ogni voce di menu in più è un invito a usare Otto
 * come un pannello di controllo. Il prodotto è l'esatto contrario — si scrive
 * cosa serve, e lui fa.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LogOut,
  MoreHorizontal,
  Plus,
  Settings as SettingsIcon,
  Sparkles,
  Trash2,
} from "lucide-react";
import type { ChatThreadView } from "@otto/shared";
import { api, chiaveAvviso, daControllare } from "./lib/api.ts";
import { messaggioDi } from "./lib/errori.ts";
import { useStore } from "./lib/store.tsx";
import { Chat } from "./views/Chat.tsx";
import { Alerts } from "./views/Alerts.tsx";
import { Accesso, bigliettoDallIndirizzo } from "./views/Accesso.tsx";
import { AvvisoPagamento, Riattiva } from "./views/Riattiva.tsx";
import { AccettaTermini } from "./views/AccettaTermini.tsx";
import { Onboarding } from "./views/Onboarding.tsx";
import { Today } from "./views/Today.tsx";
import { Inbox } from "./views/Inbox.tsx";
import { Listings } from "./views/Listings.tsx";
import { Suggestions } from "./views/Suggestions.tsx";
import { History } from "./views/History.tsx";
import { Settings } from "./views/Settings.tsx";
import { Nav } from "./shell/Nav.tsx";
import { Scorciatoie } from "./shell/Scorciatoie.tsx";
import {
  SEZIONI,
  scorciatoia,
  type Scorciatoia,
  type View,
} from "./shell/nav-items.tsx";
import { Pill, cx } from "./ui/primitives.tsx";
import { Modal } from "./ui/Modal.tsx";
import { Campanella } from "./ui/Campanella.tsx";
import { SchedaPopup } from "./views/SchedaPopup.tsx";
import { Sheet } from "./ui/Sheet.tsx";
import { SCORCIATOIE } from "./shell/nav-items.tsx";

export type { View };

/** L'indirizzo tiene la sezione: una schermata si può mandare a un collega. */
const ANCORE: Record<View, string> = {
  chat: "chat",
  alerts: "avvisi",
  settings: "impostazioni",
};

/**
 * L'ultima conversazione aperta.
 *
 * Su telefono si ricarica per mille motivi — la scheda torna in primo piano,
 * il browser libera memoria, si gira lo schermo — e ogni volta si ripartiva
 * da una conversazione vuota: quello che avevi chiesto due minuti prima era
 * ancora lì, ma bisognava andarlo a cercare.
 */
const CHIAVE_THREAD = "otto.thread";

function threadRicordato(): string | null {
  try {
    return localStorage.getItem(CHIAVE_THREAD);
  } catch {
    return null;
  }
}

/**
 * Le schede del quaderno già annunciate con l'ovale, su questo browser. Solo
 * una comodità: se si perde, un avviso esce una volta in più, niente di peggio.
 */
const CHIAVE_SCHEDE_AVVISATE = "otto.quaderno.avvisate";

function schedeAvvisate(): Set<string> {
  try {
    const grezzo = localStorage.getItem(CHIAVE_SCHEDE_AVVISATE);
    return new Set(grezzo ? (JSON.parse(grezzo) as string[]) : []);
  } catch {
    return new Set();
  }
}

function ricordaSchedeAvvisate(ids: string[]): void {
  try {
    // Le ultime duecento bastano: le schede sono poche decine per account.
    localStorage.setItem(
      CHIAVE_SCHEDE_AVVISATE,
      JSON.stringify([...new Set(ids)].slice(-200)),
    );
  } catch {
    /* modalità privata: pazienza */
  }
}

function ricordaThread(id: string | null): void {
  try {
    if (id) localStorage.setItem(CHIAVE_THREAD, id);
    else localStorage.removeItem(CHIAVE_THREAD);
  } catch {
    /* Modalità privata: si perde solo la memoria fra una ricarica e l'altra. */
  }
}

function viewFromHash(): View {
  const hash = window.location.hash.replace("#", "");
  return (
    (Object.keys(ANCORE) as View[]).find((view) => ANCORE[view] === hash) ??
    "chat"
  );
}

export function App() {
  const store = useStore();
  const [view, setView] = useState<View>(viewFromHash);
  const [threadId, setThreadIdGrezzo] = useState<string | null>(
    threadRicordato,
  );
  const [threads, setThreads] = useState<ChatThreadView[]>([]);
  /**
   * L'elenco è arrivato davvero dal server. Un elenco vuoto non dice niente
   * da solo: può essere «non ancora caricato», «caricamento fallito» o «non
   * hai conversazioni», e solo l'ultimo permette di dire che la conversazione
   * ricordata non esiste.
   */
  const [threadsLetti, setThreadsLetti] = useState(false);
  const [aperta, setAperta] = useState<Scorciatoia | null>(null);
  const [focusListing, setFocusListing] = useState<string | null>(null);
  const [focusConversazione, setFocusConversazione] = useState<string | null>(
    null,
  );
  const [altro, setAltro] = useState(false);
  const [cassetto, setCassetto] = useState(false);
  const [avvisiNuovi, setAvvisiNuovi] = useState(0);

  /** Cambiare conversazione vuol dire anche ricordarsela. */
  const setThreadId = useCallback((id: string | null) => {
    setThreadIdGrezzo(id);
    ricordaThread(id);
  }, []);

  const loadThreads = useCallback(async () => {
    try {
      const { threads: caricati } = await api.threads();
      setThreads(caricati);
      setThreadsLetti(true);
    } catch {
      setThreads([]);
    }
  }, []);

  const loadAvvisi = useCallback(async () => {
    try {
      const { nuovi } = await api.alerts();
      setAvvisiNuovi(nuovi);
    } catch {
      setAvvisiNuovi(0);
    }
  }, []);

  // Abbonamento fermo: al posto dell'app c'è «Riattiva», e niente si carica.
  const bloccato = store.account?.bloccatoPerAbbonamento === true;

  // Di ritorno da una riattivazione già confermata: si pulisce l'indirizzo e
  // si dice bentornato, una volta.
  const { notify: avvisa } = store;
  useEffect(() => {
    if (!store.ready || bloccato) return;
    const parametri = new URLSearchParams(window.location.search);
    if (!parametri.has("riattivato")) return;
    window.history.replaceState(null, "", window.location.pathname);
    avvisa("Bentornato: Otto è di nuovo attivo.");
  }, [store.ready, bloccato, avvisa]);

  useEffect(() => {
    // Senza sessione non c'è niente da chiedere: prima partivano due 401 a
    // ogni apertura della pagina di accesso.
    if (!store.ready || store.error || !store.autenticato || bloccato) return;
    void loadThreads();
    void loadAvvisi();
  }, [
    store.ready,
    store.error,
    store.autenticato,
    bloccato,
    loadThreads,
    loadAvvisi,
  ]);

  /**
   * Gli avvisi non arrivano da soli.
   *
   * Lo stream porta gli aggiornamenti delle azioni, non gli avvisi: il
   * pallino restava al numero del caricamento anche mentre le regole
   * giravano. Ricontare quando la scheda torna davanti è il modo meno
   * invasivo — nessun nuovo tipo di evento, nessuna richiesta quando nessuno
   * sta guardando.
   */
  useEffect(() => {
    // Senza sessione non c'è niente da chiedere: prima partivano due 401 a
    // ogni apertura della pagina di accesso.
    if (!store.ready || store.error || !store.autenticato || bloccato) return;

    const ricontrolla = () => {
      if (document.visibilityState === "visible") void loadAvvisi();
    };
    window.addEventListener("focus", ricontrolla);
    document.addEventListener("visibilitychange", ricontrolla);
    return () => {
      window.removeEventListener("focus", ricontrolla);
      document.removeEventListener("visibilitychange", ricontrolla);
    };
  }, [store.ready, store.error, store.autenticato, bloccato, loadAvvisi]);

  /**
   * Una conversazione **ricordata** che non esiste più non deve restare appesa.
   *
   * Solo quella ricordata, e una volta sola. Prima il controllo rigirava a
   * ogni cambio di `threadId` o di `threads`, e buttava via le conversazioni
   * appena nate: lo stream dà l'id della nuova conversazione a metà turno,
   * `loadThreads()` è una richiesta che arriva dopo, e nel frattempo
   * l'elenco è quello vecchio — che quell'id non ce l'ha. Risultato: id
   * azzerato, chat che diventa bianca e il messaggio dell'host che sparisce
   * proprio mentre Otto sta preparando la proposta, per poi ricomparire
   * quando l'elenco si aggiorna. Succedeva su ogni richiesta che porta a una
   * card, che è il momento in cui l'host guarda lo schermo più attentamente.
   *
   * Il compito vero è solo convalidare quello che arriva da `localStorage`
   * al primo elenco utile: dopo, chi mette un `threadId` sa quello che fa.
   */
  const daConvalidare = useRef<string | null>(threadRicordato());

  useEffect(() => {
    const ricordato = daConvalidare.current;
    // Si aspetta l'elenco vero, anche vuoto. Prima si aspettava un elenco
    // *non vuoto*: un account nuovo, senza conversazioni, non convalidava mai
    // e la chat provava ad aprire una conversazione di un altro account —
    // «Conversazione non trovata» a ogni avvio, su una chat mai usata.
    if (!ricordato || !threadsLetti) return;
    daConvalidare.current = null;
    if (!threads.some((t) => t.id === ricordato)) {
      setThreadIdGrezzo((corrente) =>
        corrente === ricordato ? null : corrente,
      );
      if (threadRicordato() === ricordato) ricordaThread(null);
    }
  }, [threads, threadsLetti]);

  /** Il tasto "indietro" del browser cambia sezione come un clic sul menu. */
  useEffect(() => {
    const onHash = () => setView(viewFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const go = useCallback((next: View) => {
    setView(next);
    setAperta(null);
    if (window.location.hash !== `#${ANCORE[next]}`)
      window.location.hash = ANCORE[next];
    if (next === "alerts") setAvvisiNuovi(0);
  }, []);

  /**
   * Buttare via una conversazione.
   *
   * L'elenco si ricarica dal server invece di togliere la riga a mano: se il
   * server ha rifiutato — c'è ancora una proposta che aspetta un tap — la
   * riga deve restare dov'è, e l'host deve leggere il perché.
   */
  const eliminaThread = useCallback(
    async (id: string) => {
      try {
        await api.deleteThread(id);
      } catch (errore) {
        store.notify(messaggioDi(errore));
        return;
      }
      if (threadId === id) setThreadId(null);
      await loadThreads();
    },
    [threadId, setThreadId, loadThreads, store],
  );

  const onThreadCreated = useCallback(
    (id: string) => {
      setThreadId(id);
      void loadThreads();
    },
    [loadThreads, setThreadId],
  );

  const openThread = useCallback(
    (id: string | null) => {
      setThreadId(id);
      setAltro(false);
      go("chat");
    },
    [go, setThreadId],
  );

  /** Prepara un comando e porta alla chat: preparare non è eseguire. */
  const askOtto = useCallback(
    (prompt: string) => {
      store.setPromptDraft(prompt);
      setAperta(null);
      go("chat");
    },
    [store, go],
  );

  /** Un avviso che rimanda a un immobile o a una conversazione apre la finestra giusta. */
  const apriDaAvviso = useCallback(
    (target: "inbox" | "listings", id?: string) => {
      if (target === "listings" && id) setFocusListing(id);
      // "Apri la conversazione" portava all'elenco dei messaggi e si fermava
      // lì: l'id se lo perdeva per strada, e toccava ricercare a mano proprio
      // la conversazione che l'avviso aveva già trovato.
      if (target === "inbox") setFocusConversazione(id ?? null);
      setAperta(target);
    },
    [],
  );

  // Letto una volta all'apertura: il biglietto sta nell'indirizzo del link.
  const [bigliettoDaUsare] = useState(() => bigliettoDallIndirizzo() != null);

  const { refreshAccount } = store;
  const fineOnboarding = useCallback(() => {
    void refreshAccount();
  }, [refreshAccount]);

  // ── Il quaderno: le risposte da controllare arrivano nella campanella ──
  const [quadernoAperto, setQuadernoAperto] = useState(false);
  const chiudiQuaderno = useCallback(() => setQuadernoAperto(false), []);
  const { notify } = store;
  const pronta =
    store.ready &&
    !store.error &&
    store.autenticato &&
    !bloccato &&
    store.account?.onboardingCompletato === true;
  const quadernoGuardato = useRef(0);
  /** Le schede già in campanella in questa sessione: una riga sola per scheda. */
  const quadernoInCampanella = useRef(new Set<string>());

  const controllaQuaderno = useCallback(async () => {
    // Al massimo ogni dieci minuti: il giro è settimanale, e rientrare nella
    // scheda del browser dieci volte in un'ora non deve fare dieci richieste.
    if (Date.now() - quadernoGuardato.current < 10 * 60_000) return;
    quadernoGuardato.current = Date.now();
    let inAttesa: string[];
    try {
      const { schede } = await api.quaderno();
      inAttesa = daControllare(schede).map(chiaveAvviso);
    } catch {
      return; // un invito, non una funzione: se non risponde, si tace
    }
    const nuoveQui = inAttesa.filter(
      (id) => !quadernoInCampanella.current.has(id),
    );
    if (nuoveQui.length === 0) return;
    for (const id of inAttesa) quadernoInCampanella.current.add(id);

    // L'ovale una volta sola per scheda, fra una sessione e l'altra; quelle
    // già annunciate e non ancora decise restano in campanella, zitte.
    const avvisate = schedeAvvisate();
    const maiAvvisate = inAttesa.filter((id) => !avvisate.has(id));
    ricordaSchedeAvvisate([...avvisate, ...inAttesa]);

    const n = inAttesa.length;
    notify(
      n === 1
        ? "Ho 1 risposta da farti controllare"
        : `Ho ${n} risposte da farti controllare`,
      {
        azione: { etichetta: "Apri", esegui: () => setQuadernoAperto(true) },
        silenziosa: maiAvvisate.length === 0,
      },
    );
  }, [notify]);

  useEffect(() => {
    if (!pronta) return;
    void controllaQuaderno();
    const ricontrolla = () => {
      if (document.visibilityState === "visible") void controllaQuaderno();
    };
    document.addEventListener("visibilitychange", ricontrolla);
    return () => document.removeEventListener("visibilitychange", ricontrolla);
  }, [pronta, controllaQuaderno]);

  const pending = useMemo(
    () =>
      [...store.actions.values()].filter((a) => a.status === "proposta").length,
    [store.actions],
  );

  if (!store.ready) return <Booting />;
  // Un link di conferma o di nuova password vale anche per chi ha già una
  // sessione aperta su questo browser: il biglietto è di chi l'ha aperto.
  if (bigliettoDaUsare)
    return <Accesso onEntrato={() => window.location.reload()} />;
  if (store.error) return <Broken message={store.error} />;
  // Ricaricare dopo l'accesso è il modo più semplice e più affidabile di
  // ripartire pulito: lo store rifà il suo giro, lo stream si riapre, e non
  // resta in giro nessuno stato della sessione di prima.
  if (!store.autenticato)
    return <Accesso onEntrato={() => window.location.reload()} />;
  if (bloccato)
    return <Riattiva abbonamento={store.account?.abbonamento ?? null} />;
  // I Termini prima di tutto il resto, onboarding compreso: chi entra per la
  // prima volta non ha mai visto un modulo di registrazione.
  if (store.utente?.terminiDaAccettare)
    return (
      <AccettaTermini
        nome={store.utente.nome}
        onAccettati={store.refreshAccount}
      />
    );

  // Finché il gestionale non è collegato e importato, l'app non ha niente da
  // mostrare: si mostra l'onboarding al suo posto.
  if (store.account && !store.account.onboardingCompletato)
    return (
      <Onboarding nome={store.utente?.nome ?? null} onFinito={fineOnboarding} />
    );

  const finestra = aperta ? scorciatoia(aperta) : null;

  return (
    <div className="flex h-full overflow-hidden bg-surface">
      {/* ── Colonna scura ──────────────────────────────────────────────── */}
      <div className="hidden lg:flex">
        <Nav
          view={view}
          onGo={go}
          nomeUtente={
            store.utente?.nome?.trim() ||
            store.utente?.email.split("@")[0] ||
            store.account?.nome ||
            "Tu"
          }
          badges={{ alerts: avvisiNuovi }}
          killSwitch={store.account?.killSwitch ?? false}
          threads={threads}
          threadId={threadId}
          onOpenThread={openThread}
          onEliminaThread={eliminaThread}
        />
      </div>

      {/* ── Contenuto ──────────────────────────────────────────────────── */}
      <main className="flex min-w-0 flex-1 pb-[var(--altezza-barra)] installata:pb-[calc(var(--altezza-barra)+env(safe-area-inset-bottom))] lg:pb-0">
        <div className="flex min-w-0 flex-1 flex-col">
          {store.account?.abbonamento === "past_due" ? (
            <AvvisoPagamento />
          ) : null}
          {view === "chat" ? (
            <Chat
              threadId={threadId}
              onNuova={() => openThread(null)}
              onConversazioni={() => setCassetto(true)}
              onThreadCreated={onThreadCreated}
              onThreadMancante={(id) => {
                setThreadIdGrezzo((corrente) =>
                  corrente === id ? null : corrente,
                );
                if (threadRicordato() === id) ricordaThread(null);
              }}
            />
          ) : view === "alerts" ? (
            <Alerts onAskOtto={askOtto} onOpen={apriDaAvviso} />
          ) : (
            <Pagina titolo="Impostazioni" larga>
              <Settings />
            </Pagina>
          )}
        </div>

        {/* Le scorciatoie appartengono alla chat: sono il modo di sbirciare
            altrove senza uscire dalla conversazione. Nelle altre sezioni
            sarebbero solo una colonna di icone in più. */}
        {view === "chat" ? <Scorciatoie onApri={setAperta} /> : null}
      </main>

      {/* ── Le scorciatoie, in finestra ────────────────────────────────── */}
      <Modal
        open={finestra != null}
        onClose={() => setAperta(null)}
        title={finestra?.label ?? ""}
        subtitle={finestra?.descrizione}
        bleed={finestra?.pieno}
        sopraLaBarra
        media={
          finestra ? (
            <span className="grid size-10 shrink-0 place-items-center rounded-field bg-surface-muted text-ink-soft">
              <finestra.Icon size={18} />
            </span>
          ) : null
        }
      >
        {aperta === "today" ? (
          <Today
            onGoTo={(target) =>
              target === "chat" ? askOtto("") : setAperta(target)
            }
          />
        ) : null}
        {aperta === "inbox" ? (
          <Inbox onAskOtto={askOtto} focusId={focusConversazione} />
        ) : null}
        {aperta === "listings" ? (
          <Listings onAskOtto={askOtto} focusId={focusListing} />
        ) : null}
        {aperta === "suggestions" ? (
          <Suggestions
            onAskOtto={askOtto}
            onOpenListing={(id) => apriDaAvviso("listings", id)}
          />
        ) : null}
        {aperta === "history" ? <History /> : null}
      </Modal>

      <SchedaPopup aperta={quadernoAperto} onChiudi={chiudiQuaderno} />

      <BarraMobile
        view={view}
        aperta={aperta}
        onGo={go}
        onApri={(id) => {
          setAltro(false);
          setAperta(id);
        }}
        onAltro={() => setAltro(true)}
        avvisi={avvisiNuovi}
        pending={pending}
      />

      <CassettoConversazioni
        aperto={cassetto}
        onChiudi={() => setCassetto(false)}
        threads={threads}
        threadId={threadId}
        onApriThread={(id) => {
          setCassetto(false);
          openThread(id);
        }}
        onEliminaThread={eliminaThread}
      />

      <Altro
        aperto={altro}
        onChiudi={() => setAltro(false)}
        onApri={(id) => {
          setAltro(false);
          setAperta(id);
        }}
        onImpostazioni={() => {
          setAltro(false);
          go("settings");
        }}
      />
    </div>
  );
}

/** Una sezione a colonna singola, con il suo titolo. */
function Pagina({
  titolo,
  larga,
  children,
}: {
  titolo: string;
  /** Per le pagine a più colonne: le impostazioni non stanno in 900 px. */
  larga?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="scroll-soft h-full overflow-x-hidden overflow-y-auto">
      <div
        className={cx(
          "mx-auto w-full px-5 pb-12 pt-7 sm:px-8",
          larga ? "max-w-[1280px] xl:px-12" : "max-w-[900px]",
        )}
      >
        <div className="mb-5 flex items-center justify-between gap-3">
          <h1 className="text-[26px] font-extrabold tracking-tight text-ink">
            {titolo}
          </h1>
          <Campanella />
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Barra in basso (schermo stretto) ────────────────────────────────────────

function BarraMobile({
  view,
  aperta,
  onGo,
  onApri,
  onAltro,
  avvisi,
  pending,
}: {
  view: View;
  aperta: Scorciatoia | null;
  onGo: (view: View) => void;
  onApri: (id: Scorciatoia) => void;
  onAltro: () => void;
  avvisi: number;
  pending: number;
}) {
  // Sei tasti: le tre cose che un host apre dieci volte al giorno (chat,
  // avvisi, messaggi), poi immobili e oggi. Le impostazioni, i consigli e lo
  // storico stanno dietro «Altro»: si aprono una volta a settimana.
  const tasto =
    "relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-card px-0.5 transition-colors duration-quick";
  const inBarra = SCORCIATOIE.filter((voce) =>
    (["inbox", "listings", "today"] as Scorciatoia[]).includes(voce.id),
  );
  return (
    <nav className="fixed inset-x-0 bottom-0 z-30 flex h-[var(--altezza-barra)] border-t border-line bg-surface px-1 installata:h-[calc(var(--altezza-barra)+env(safe-area-inset-bottom))] installata:pb-[env(safe-area-inset-bottom)] lg:hidden">
      {SEZIONI.filter((voce) => voce.id !== "settings").map((voce) => {
        const attivo = view === voce.id && !aperta;
        return (
          <button
            key={voce.id}
            type="button"
            onClick={() => onGo(voce.id)}
            className={cx(tasto, attivo ? "text-accent" : "text-ink-faint")}
          >
            <voce.Icon size={18} strokeWidth={attivo ? 2.4 : 2} />
            <span className="text-[9.5px] font-bold leading-none">
              {voce.label}
            </span>
            {voce.id === "alerts" && avvisi > 0 ? (
              <span className="absolute right-2 top-0.5 size-2 rounded-full bg-danger ring-2 ring-surface" />
            ) : null}
          </button>
        );
      })}
      {inBarra.map((voce) => {
        const attivo = aperta === voce.id;
        return (
          <button
            key={voce.id}
            type="button"
            onClick={() => onApri(voce.id)}
            className={cx(tasto, attivo ? "text-accent" : "text-ink-faint")}
          >
            <voce.Icon size={18} strokeWidth={attivo ? 2.4 : 2} />
            <span className="text-[9.5px] font-bold leading-none">
              {voce.label}
            </span>
          </button>
        );
      })}

      {/* «Altro» non è una sezione nuova: è la porta per impostazioni,
          consigli, storico, conversazioni ed Esci. */}
      <button
        type="button"
        onClick={onAltro}
        aria-label="Altro"
        className={cx(tasto, "text-ink-faint")}
      >
        <MoreHorizontal size={18} />
        <span className="text-[9.5px] font-bold leading-none">Altro</span>
        {pending > 0 ? (
          <span className="absolute right-2 top-0.5 size-2 rounded-full bg-ink ring-2 ring-surface" />
        ) : null}
      </button>
    </nav>
  );
}

/**
 * "Altro": quello che su schermo largo sta altrove.
 *
 * Sotto i 1024 px la colonna scura non c'è, e con lei sparivano l'elenco
 * delle conversazioni, "Nuova conversazione" e "Esci"; sotto i 640 px
 * sparivano anche Immobili e Storico, perché la colonna delle scorciatoie si
 * nasconde. Un property manager che apre Otto dal telefono in mezzo a un
 * check-in è il caso d'uso principale, non un ripiego.
 *
 * Non è una voce di menu in più — il menu resta di tre — è la stessa roba,
 * raggiunta da un pannello che si apre e si richiude.
 */
/**
 * Le conversazioni con Otto, una per riga, con il cestino. Vive nel cassetto
 * della chat: nel pannello «Altro» non c'è apposta, lì stanno solo le
 * schermate (deciso il 9/10/2026).
 */
function ElencoConversazioni({
  threads,
  threadId,
  onApriThread,
  onEliminaThread,
}: {
  threads: ChatThreadView[];
  threadId: string | null;
  onApriThread: (id: string | null) => void;
  onEliminaThread: (id: string) => void | Promise<void>;
}) {
  // Cancellare una conversazione si conferma sul posto: una finestra di
  // sistema qui sarebbe la sola del prodotto, e su telefono copre tutto.
  const [daCancellare, setDaCancellare] = useState<string | null>(null);

  return (
    <>
      {threads.length === 0 ? (
        <p className="px-1 text-[13px] leading-relaxed text-ink-soft">
          Nessuna conversazione. Scrivi in chat quello che ti serve.
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {threads.map((thread) => (
            <div
              key={thread.id}
              className={cx(
                "flex items-center gap-1 rounded-card pr-1",
                thread.id === threadId
                  ? "bg-accent-soft"
                  : "active:bg-surface-muted",
              )}
            >
              <button
                type="button"
                onClick={() => onApriThread(thread.id)}
                className={cx(
                  "min-w-0 flex-1 truncate px-3 py-2 text-left text-[13.5px] font-medium",
                  thread.id === threadId ? "text-ink" : "text-ink-soft",
                )}
              >
                {thread.title}
              </button>
              {daCancellare === thread.id ? (
                <span className="flex shrink-0 items-center gap-1 pl-1 text-[12px]">
                  <button
                    type="button"
                    onClick={() => {
                      setDaCancellare(null);
                      void onEliminaThread(thread.id);
                    }}
                    className="rounded-field px-2 py-1 font-bold text-danger"
                  >
                    Cancella
                  </button>
                  <button
                    type="button"
                    onClick={() => setDaCancellare(null)}
                    className="rounded-field px-2 py-1 text-ink-soft"
                  >
                    No
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={`Cancella «${thread.title}»`}
                  onClick={() => setDaCancellare(thread.id)}
                  className="grid size-8 shrink-0 place-items-center rounded-field text-ink-faint active:bg-surface-muted"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Il cassetto delle conversazioni, da sinistra sopra la chat: si apre con le
 * tre barrette nell'intestazione e si chiude toccando fuori o la X. È la
 * colonna delle conversazioni del PC, per chi la colonna non ce l'ha.
 */
export function CassettoConversazioni({
  aperto,
  onChiudi,
  threads,
  threadId,
  onApriThread,
  onEliminaThread,
}: {
  aperto: boolean;
  onChiudi: () => void;
  threads: ChatThreadView[];
  threadId: string | null;
  onApriThread: (id: string | null) => void;
  onEliminaThread: (id: string) => void | Promise<void>;
}) {
  return (
    <Sheet
      open={aperto}
      onClose={onChiudi}
      title="Conversazioni"
      lato="sinistra"
      footer={
        <Pill
          variant="secondary"
          icon={<Plus size={15} />}
          onClick={() => onApriThread(null)}
          className="w-full justify-center"
        >
          Nuova conversazione
        </Pill>
      }
    >
      <div className="flex flex-col pb-2">
        <ElencoConversazioni
          threads={threads}
          threadId={threadId}
          onApriThread={onApriThread}
          onEliminaThread={onEliminaThread}
        />
      </div>
    </Sheet>
  );
}

export function Altro({
  aperto,
  onChiudi,
  onApri,
  onImpostazioni,
}: {
  aperto: boolean;
  onChiudi: () => void;
  onApri: (id: Scorciatoia) => void;
  onImpostazioni: () => void;
}) {
  return (
    <Sheet open={aperto} onClose={onChiudi} title="Altro">
      <div className="flex flex-col gap-5 pb-2">
        {/* ── Le schermate ────────────────────────────────────────────── */}
        <div>
          <p className="pb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
            Schermate
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onImpostazioni}
              className="flex items-center gap-2.5 rounded-card bg-surface-muted px-3 py-2.5 text-left transition-colors duration-quick active:bg-surface-sunken"
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-field bg-surface text-ink-soft">
                <SettingsIcon size={16} />
              </span>
              <span className="min-w-0 text-[13.5px] font-bold text-ink">
                Impostazioni
              </span>
            </button>
            {/* Messaggi, Immobili e Oggi stanno nella barra in basso. */}
            {SCORCIATOIE.filter(
              (voce) => voce.id === "suggestions" || voce.id === "history",
            ).map((voce) => (
              <button
                key={voce.id}
                type="button"
                onClick={() => onApri(voce.id)}
                className="flex items-center gap-2.5 rounded-card bg-surface-muted px-3 py-2.5 text-left transition-colors duration-quick active:bg-surface-sunken"
              >
                <span className="grid size-8 shrink-0 place-items-center rounded-field bg-surface text-ink-soft">
                  <voce.Icon size={16} />
                </span>
                <span className="min-w-0 text-[13.5px] font-bold text-ink">
                  {voce.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Uscire ──────────────────────────────────────────────────── */}
        <Pill
          variant="ghost"
          icon={<LogOut size={15} />}
          onClick={() => {
            localStorage.removeItem("otto.token");
            localStorage.removeItem(CHIAVE_THREAD);
            window.location.reload();
          }}
          className="self-start"
        >
          Esci
        </Pill>
      </div>
    </Sheet>
  );
}

// ── Stati globali ───────────────────────────────────────────────────────────

function Booting() {
  return (
    <div className="grid h-full place-items-center">
      <div className="flex flex-col items-center gap-3">
        <div className="grid size-12 place-items-center rounded-card bg-accent text-ink-inverse shadow-lift">
          <Sparkles size={22} />
        </div>
        <span className="text-[13px] font-medium text-ink-soft">
          Un attimo…
        </span>
      </div>
    </div>
  );
}

function Broken({ message }: { message: string }) {
  return (
    <div className="grid h-full place-items-center px-6">
      <div className="max-w-sm rounded-card-lg bg-surface p-7 text-center shadow-lift ring-1 ring-line">
        <p className="text-[16px] font-extrabold text-ink">
          Non riesco a partire
        </p>
        <p className="mt-2 text-[14px] leading-relaxed text-ink-soft">
          {message}
        </p>
        <Pill
          variant="primary"
          className="mt-5"
          onClick={() => window.location.reload()}
        >
          Riprova
        </Pill>
      </div>
    </div>
  );
}
